'use client';

import {
	DataGridPremium,
	GridPaginationModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { useQuery } from '@parkchirp/api-client';
import { FC, useCallback, useEffect, useState } from 'react';

import { GET_PARKERS_BY_COUNT_AND_RATE, GET_PARKERS_BY_COUNT_AND_RATE_CSV } from '../../../queries';
import { datagridFilterPanelAttributes } from '../../../utils';
import { CsvExportButtonV2 } from '../../reports';
import { getUtilizationDetailsColumns } from './utilizationDetailsColumns';

type UtilizationDetailsTableProps = {
	leasingCountAndRateId: number;
	facilityId: string;
	getUrlToSubscriptionPage?: (...x: any) => string;
	initialData?: any;
};

export const UtilizationDetailsTable: FC<UtilizationDetailsTableProps> = ({
	leasingCountAndRateId,
	facilityId,
	getUrlToSubscriptionPage,
	initialData,
}) => {
	const [rows, setRows] = useState<any[]>([]);
	const [filters, setFilters] = useState<any[]>([]);
	const [sortOrder, setSortOrder] = useState({ field: 'lastName', sort: 'asc' });
	const [rowCount, setRowCount] = useState<number>();
	const [{ page, pageSize }, setPaginationModel] = useState<GridPaginationModel>({
		pageSize: 50,
		page: 0,
	});

	const { data: parkerData, loading } = useQuery(GET_PARKERS_BY_COUNT_AND_RATE, {
		fetchPolicy: 'network-only',
		variables: {
			leasingCountAndRateId,
			filters,
			order: sortOrder,
			limit: pageSize,
			offset: page * pageSize,
		},
		skip: initialData,
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});
	const data = initialData || parkerData;

	useEffect(() => {
		if (!loading && data) {
			setRows(data?.getParkersByCountAndRate?.parkers);
			setRowCount(data?.getParkersByCountAndRate?.total);
		}
	}, [loading, data]);

	useEffect(() => {
		setRowCount((prevCount) => (rowCount !== undefined ? rowCount : prevCount));
	}, [rowCount]);

	const onSortModelChange = useCallback(
		(sortModel) => {
			if (!sortModel.length || !sortModel[0]?.sort) {
				return;
			}

			setPaginationModel((prev) => ({ ...prev, page: 0 }));
			const { field, sort } = sortModel[0];

			setSortOrder({ field, sort });
		},
		[setPaginationModel, setSortOrder],
	);

	const onFilterModelChange = useCallback(
		(filterModel) => {
			const { items: filterItems } = filterModel;

			if (!filterItems.length) {
				setFilters([]);
				return;
			}

			setPaginationModel((prev) => ({ ...prev, page: 0 }));

			const queryFilters = filterItems
				.map((filter) => {
					const { field, operator, value } = filter;

					if (!value && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];

					return {
						propertyName: field,
						op: operator,
						value: value ?? null,
					};
				})
				.flat();

			if (queryFilters.length) {
				setFilters(queryFilters);
			}
		},
		[setFilters, setPaginationModel],
	);

	const customToolbar = () => (
		<GridToolbarContainer>
			<GridToolbarFilterButton />
			<GridToolbarColumnsButton />
			<GridToolbarDensitySelector />
			<CsvExportButtonV2
				disabled={loading}
				query={GET_PARKERS_BY_COUNT_AND_RATE_CSV}
				queryName='getParkersByCountAndRateCsv'
				args={{
					variables: {
						leasingCountAndRateId,
						filters,
						order: sortOrder,
						limit: pageSize,
						offset: page * pageSize,
					},
					context: {
						apiVersion: 'v2',
					},
				}}
			/>
		</GridToolbarContainer>
	);

	const columns = getUtilizationDetailsColumns({
		facilityId,
		getUrlToSubscriptionPage,
	});

	return (
		<div className='pca__datagrid-sticky--headfoot'>
			<DataGridPremium
				slots={{
					toolbar: customToolbar,
				}}
				slotProps={{
					filterPanel: { ...datagridFilterPanelAttributes },
				}}
				disableRowSelectionOnClick
				rows={rows}
				columns={columns}
				loading={loading}
				rowCount={rowCount || 0}
				sortingMode='server'
				onSortModelChange={onSortModelChange}
				filterMode='server'
				onFilterModelChange={onFilterModelChange}
				pagination
				paginationMode='server'
				pageSizeOptions={[pageSize]}
				paginationModel={{ page, pageSize }}
				onPaginationModelChange={setPaginationModel}
				sx={{ height: '100%' }}
			/>
		</div>
	);
};
